import { Flex, Avatar, Image, Text, Skeleton, Box } from '@chakra-ui/react';
import { Response } from 'datx-jsonapi';
import { useResourcesInfinite } from 'datx-jsonapi-react';
import { FC, Fragment, useMemo } from 'react';
import { InView } from 'react-intersection-observer';

import { SearchInput as SharedSearchInput } from '@/components/ui/shared/inputs/SearchInput/SearchInput';
import { useLocalSearch } from '@/hooks/use-search/useLocalSearch';
import { StringParam, useQueryParams } from '@/hooks/useQueryParams';
import useSession from '@/hooks/useSession';
import { queryThinProfileInfinite } from '@/queries/thin-profiles';
import { ThinProfile } from '@/resources/ThinProfile';
import { extractSWRInfiniteData } from '@/utils/swr/extractSWRInfiniteData';

interface IUserList {
  setProfileSelected: (profile: ThinProfile) => void;
}

const PAGE_SIZE = 12;

export const UserList: FC<IUserList> = ({ setProfileSelected }) => {
  const { searchTerm, ref: input } = useLocalSearch();
  const { user } = useSession();
  const { eventId } = useQueryParams<{
    eventId: string;
  }>({
    eventId: StringParam,
  });

  const { data: profiles, error, size, setSize } = useResourcesInfinite(
    queryThinProfileInfinite(
      {
        query: searchTerm,
        with_ticket_for_event: eventId,
        searchable_attribute: ['first_name', 'last_name', 'institutions.name'],
      },
      PAGE_SIZE
    )
  );

  const { isLoadingMore, isReachingEnd, items, isEmpty } = useMemo(
    () => extractSWRInfiniteData<ThinProfile>(profiles as Array<Response<ThinProfile>>, error, size),
    [profiles, error, size]
  );

  const itemsWithoutMe = items.filter((i) => i.user.id !== user.id);

  const noResults = !isLoadingMore && itemsWithoutMe.length === 0 && searchTerm;

  return (
    <Fragment>
      <SharedSearchInput
        ref={input}
        w="full"
        bg="white"
        mt={6}
        color="gray.700"
        borderRadius="full"
        placeholder="search people"
      />
      <Flex flexDirection="column" overflowY="auto" flex={1} mt={5}>
        {noResults && <Text mt={2}>No search results found for &quot;{searchTerm}&quot;</Text>}
        {itemsWithoutMe.map((profile) => (
          <Flex
            onClick={() => setProfileSelected(profile)}
            _hover={{ boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.08)' }}
            cursor="pointer"
            mb={2}
            key={profile.id}
          >
            {!profile.avatar && <Avatar bg="violet.500" color="white" name={profile.fullName} />}
            {profile.avatar && (
              <Image
                src={profile.avatar}
                alt={profile.fullName}
                width={10}
                height={10}
                style={{ borderRadius: '50%' }}
              />
            )}
            <Flex ml={2} flexDirection="column" justifyContent="center">
              <Text fontWeight="bold" fontFamily="heading">
                {profile.fullName}
              </Text>
              <Text fontSize="xs">{profile.title}</Text>
            </Flex>
          </Flex>
        ))}
        {isLoadingMore &&
          Array.from({ length: PAGE_SIZE }, (_, i) => (
            <Flex alignItems="center" mb={2} key={i}>
              <Skeleton borderRadius="50%" h={12} w={12} />
              <Flex ml={2} gap={2} flexDirection="column">
                <Skeleton w={40} h={3} />
                <Skeleton w={52} h={2} />
              </Flex>
            </Flex>
          ))}
        {!isLoadingMore && (
          <InView
            onChange={(inView) => {
              if (inView && !error && !isReachingEnd) {
                setSize(size + 1);
              }
            }}
          >
            <Box w={1} h={1} />
          </InView>
        )}
      </Flex>
    </Fragment>
  );
};
